import { icon } from '../icons.js';
import { ASSESSMENT_SECTIONS } from './content.js';
import { renderQuestionControl } from './controls.js';
import { renderReview } from './review.js';

function element(tagName, options = {}, children = []) {
  const node = document.createElement(tagName);
  if (options.className) node.className = options.className;
  if (options.text !== undefined) node.textContent = options.text;
  for (const [name, value] of Object.entries(options.attributes || {})) {
    if (value !== null && value !== undefined) node.setAttribute(name, String(value));
  }
  for (const child of children) {
    if (child) node.append(child);
  }
  return node;
}

function sectionIndex(sectionId) {
  return ASSESSMENT_SECTIONS.findIndex(({ id }) => id === sectionId);
}

function saveStatus(saveState) {
  return element('div', { className: 'assessment-save-state', attributes: { role: 'status' } }, [
    element('span', { className: 'assessment-save-state__dot', attributes: { 'aria-hidden': 'true' } }),
    element('span', { text: saveState || 'Ready' }),
  ]);
}

function brandHeader(state) {
  const menu = element('button', {
    className: 'assessment-menu-button',
    attributes: {
      type: 'button',
      'aria-label': state.drawerOpen ? 'Close section menu' : 'Open section menu',
      'aria-expanded': state.drawerOpen ? 'true' : 'false',
      'aria-controls': 'assessment-navigation',
    },
  }, [icon(state.drawerOpen ? 'close' : 'menu')]);
  menu.addEventListener('click', () => state.onDrawerToggle?.(!state.drawerOpen));

  return element('header', { className: 'assessment-header' }, [
    element('div', { className: 'assessment-header__inner' }, [
      element('img', {
        className: 'assessment-logo',
        attributes: { src: '/pub-invest-group.png', alt: 'Pub Invest Group' },
      }),
      element('div', { className: 'assessment-product' }, [
        element('strong', { text: 'Operations Leadership' }),
        element('span', { text: 'Candidate workspace' }),
      ]),
      element('div', { className: 'assessment-candidate' }, [
        element('span', { text: `Welcome, ${state.candidate.firstName}` }),
        saveStatus(state.saveState),
      ]),
      menu,
    ]),
  ]);
}

function navigation(state) {
  const activeIndex = sectionIndex(state.activeSectionId);
  const progress = Math.max(1, Math.round(((activeIndex + 1) / ASSESSMENT_SECTIONS.length) * 100));
  const list = element('ol', { className: 'assessment-nav__list' });

  ASSESSMENT_SECTIONS.forEach((section, index) => {
    const locked = index > state.unlockedSectionIndex;
    const completed = state.completedSectionIds?.includes(section.id);
    const button = element('button', {
      className: [
        'assessment-nav__button',
        index === activeIndex ? 'is-active' : '',
        completed ? 'is-complete' : '',
      ].filter(Boolean).join(' '),
      attributes: {
        type: 'button',
        disabled: locked ? '' : null,
        'aria-current': index === activeIndex ? 'step' : null,
        'aria-label': locked ? `${section.title}, locked` : null,
      },
    }, [
      element('span', { className: 'assessment-nav__number', text: String(section.number).padStart(2, '0') }),
      element('span', { className: 'assessment-nav__name', text: section.title }),
      locked ? icon('lock', 'assessment-nav__status') : completed ? icon('check', 'assessment-nav__status') : null,
    ]);
    if (!locked) button.addEventListener('click', () => state.onNavigate(section.id));
    list.append(element('li', {}, [button]));
  });

  return element('aside', {
    className: `assessment-nav${state.drawerOpen ? ' is-open' : ''}`,
    attributes: { id: 'assessment-navigation', 'aria-label': 'Assessment sections' },
  }, [
    element('div', { className: 'assessment-nav__top' }, [
      element('div', { className: 'assessment-nav__label' }, [
        element('span', { text: 'Candidate journey' }),
        element('strong', { text: `${progress}%` }),
      ]),
      element('div', { className: 'assessment-progress', attributes: { role: 'progressbar', 'aria-valuenow': progress, 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-label': 'Assessment progress' } }, [
        element('span', { attributes: { style: `width: ${progress}%` } }),
      ]),
    ]),
    list,
    element('p', {
      className: 'assessment-nav__note',
      text: 'You can stop between sections and return on another device. Nothing is sent for review until you submit the completed assessment.',
    }),
  ]);
}

function sectionHeader(section) {
  return element('header', { className: 'assessment-section-header' }, [
    element('p', {
      className: 'assessment-eyebrow',
      text: section.eyebrow || `SECTION ${String(section.number).padStart(2, '0')} OF 09 · ${section.estimateMinutes} MIN`,
    }),
    element('h1', { text: section.heading || section.title, attributes: { tabindex: '-1' } }),
    section.purpose ? element('p', { className: 'assessment-section-intro', text: section.purpose }) : null,
    section.opening ? element('p', { className: 'assessment-section-intro', text: section.opening }) : null,
    section.responsePattern ? element('div', { className: 'response-pattern' }, [
      element('strong', { text: 'Build the operational answer' }),
      element('p', { text: section.responsePattern }),
    ]) : null,
  ]);
}

function introduction(section) {
  const content = section.content;
  return element('div', { className: 'introduction-layout' }, [
    element('p', { className: 'introduction-lead', text: content.opening }),
    element('div', { className: 'introduction-columns' }, [
      element('section', { className: 'editorial-panel' }, [
        element('span', { text: '01' }),
        element('h2', { text: 'The opportunity' }),
        element('p', { text: content.opportunity }),
      ]),
      element('section', { className: 'editorial-panel' }, [
        element('span', { text: '02' }),
        element('h2', { text: 'The reality' }),
        element('p', { text: content.reality }),
      ]),
    ]),
    element('aside', { className: 'approach-note' }, [
      element('span', { text: 'How to approach it' }),
      element('p', { text: content.approach }),
      element('section', { className: 'ownership-definition' }, [
        element('h2', { text: content.ownership.title }),
        element('p', { text: content.ownership.body }),
      ]),
    ]),
    element('section', { className: 'process-facts' }, [
      element('h2', { text: 'Before you begin' }),
      element('ul', {}, content.processFacts.map((fact) => element('li', {}, [
        icon('check'),
        element('span', { text: fact }),
      ]))),
    ]),
  ]);
}

function formatCurrency(value) {
  return `£${new Intl.NumberFormat('en-GB').format(value)}`;
}

function commercialData(section) {
  const columns = [
    ['salesActual', 'Sales actual', formatCurrency],
    ['salesBudget', 'Forecast', formatCurrency],
    ['likeForLikeSales', 'YOY', (value) => `${value > 0 ? '+' : ''}${value}%`],
    ['labourActual', 'Wage percentage', (value) => `${value}%`],
    ['labourTarget', 'Wage target', (value) => `${value}%`],
    ['grossProfitActual', 'GP', (value) => `${value}%`],
    ['grossProfitTarget', 'GP target', (value) => `${value}%`],
    ['guestComplaints', 'Complaints', String],
    ['voids', 'Voids', (value) => `${value}%`],
    ['teamTurnover', 'Staff turnover', (value) => `${value}%`],
    ['venueHealthCheck', 'Venue Health Check', String],
  ];
  const table = element('table', { className: 'commercial-table' }, [
    element('caption', { text: 'Fictional three-venue weekly data pack' }),
    element('thead', {}, [element('tr', {}, [
      element('th', { text: 'Venue', attributes: { scope: 'col' } }),
      ...columns.map(([, label]) => element('th', { text: label, attributes: { scope: 'col' } })),
    ])]),
    element('tbody', {}, Object.entries(section.dataPack).map(([venue, data]) => element('tr', {}, [
      element('th', { text: venue, attributes: { scope: 'row' } }),
      ...columns.map(([key, , formatter]) => element('td', { text: formatter(data[key]) })),
    ]))),
  ]);

  return element('section', { className: 'data-pack', attributes: { 'aria-labelledby': 'data-pack-title' } }, [
    element('div', { className: 'data-pack__head' }, [
      element('div', {}, [
        element('p', { className: 'assessment-eyebrow', text: 'Fictional operating data' }),
        element('h2', { text: 'Three venues. One patch.', attributes: { id: 'data-pack-title' } }),
      ]),
      element('p', { text: 'Read across the full picture before choosing the loudest number.' }),
    ]),
    element('div', { className: 'commercial-table-wrap', attributes: { tabindex: '0', 'aria-label': 'Scrollable commercial data table' } }, [table]),
    element('dl', { className: 'commercial-glossary' }, Object.entries(section.glossary).flatMap(([term, definition]) => [
      element('dt', { text: ({ likeForLike: 'YOY', grossProfit: 'GP', voids: 'Voids', rollingTurnover: 'Staff turnover' })[term] }),
      element('dd', { text: definition }),
    ])),
  ]);
}

function questionList(section, state) {
  return element('div', { className: 'assessment-questions' }, section.questions.map((question) =>
    renderQuestionControl({
      question,
      value: state.answers[question.id],
      error: state.errors[question.id],
      onChange: state.onChange,
    })));
}

function actionBar(section, state) {
  const index = sectionIndex(section.id);
  const previous = index > 0 ? ASSESSMENT_SECTIONS[index - 1] : null;
  const next = ASSESSMENT_SECTIONS[index + 1];
  const isReview = section.id === 'review-submit';
  const bar = element('footer', {
    className: `assessment-action-bar${isReview ? ' assessment-action-bar--review' : ''}`,
  });
  const position = element('span', {
    className: 'assessment-action-bar__position',
    text: `${String(section.number).padStart(2, '0')} / 09`,
  });
  bar.append(position);

  if (isReview) {
    bar.append(element('p', {
      className: 'assessment-action-bar__readiness',
      text: state.submissionReadinessMessage,
      attributes: {
        id: 'submission-readiness',
        'aria-live': 'polite',
        'data-submission-readiness': '',
      },
    }));
  }

  const actions = element('div', { className: 'assessment-action-bar__actions' });
  if (previous) {
    const back = element('button', {
      className: 'assessment-button assessment-button--secondary',
      attributes: { type: 'button' },
    }, [icon('back'), element('span', { text: 'Previous' })]);
    back.addEventListener('click', () => state.onNavigate(previous.id));
    actions.append(back);
  }
  if (next || section.id === 'review-submit') {
    const label = section.id === 'introduction'
      ? section.content.primaryAction
      : section.id === 'review-submit'
        ? 'Submit my assessment'
        : `Continue to ${next.title}`;
    const continueButton = element('button', {
      className: 'assessment-button assessment-button--primary',
      attributes: {
        type: 'button',
        'data-action': section.id === 'review-submit' ? 'open-submission' : 'continue',
        disabled: section.id === 'review-submit' && !state.assessmentReady ? '' : null,
        'aria-describedby': section.id === 'review-submit' ? 'submission-readiness' : null,
      },
    }, [element('span', { text: label }), icon('forward')]);
    continueButton.addEventListener('click', state.onContinue);
    actions.append(continueButton);
  }
  bar.append(actions);
  return bar;
}

function sectionContent(section, state) {
  const body = section.id === 'introduction'
    ? introduction(section)
    : section.id === 'review-submit'
      ? renderReview(section, state)
      : element('div', {}, [
        section.id === 'commercial-thinking' ? commercialData(section) : null,
        questionList(section, state),
      ]);

  return element('main', {
    className: 'assessment-main',
    attributes: { id: 'assessment-main', tabindex: '-1' },
  }, [
    element('div', { className: 'assessment-main__content' }, [sectionHeader(section), body]),
    actionBar(section, state),
  ]);
}

export function renderWorkspace(state) {
  const section = ASSESSMENT_SECTIONS.find(({ id }) => id === state.activeSectionId)
    || ASSESSMENT_SECTIONS[0];
  const skipLink = element('a', {
    className: 'assessment-skip-link',
    text: 'Skip to assessment content',
    attributes: { href: '#assessment-main' },
  });
  skipLink.addEventListener('click', (event) => {
    event.preventDefault();
    const target = document.querySelector('#assessment-main');
    target?.focus();
    target?.scrollIntoView({ block: 'start' });
  });
  const backdrop = element('button', {
    className: `assessment-backdrop${state.drawerOpen ? ' is-visible' : ''}`,
    attributes: { type: 'button', 'aria-label': 'Close section menu', tabindex: state.drawerOpen ? '0' : '-1' },
  });
  backdrop.addEventListener('click', () => state.onDrawerToggle?.(false));

  return element('div', { className: 'assessment-workspace' }, [
    skipLink,
    brandHeader(state),
    element('div', { className: 'assessment-shell' }, [
      navigation(state),
      sectionContent(section, state),
    ]),
    backdrop,
  ]);
}
